/* hc-predictive-search.js
 *
 * Help Center hero search — in-browser ranked search over a COMPLETE,
 * HC-exclusive index emitted at render time inside this element as
 * <script type="application/json" id="hc-search-index"> (see
 * snippets/help-center-hero.liquid). No /search/suggest fetch: that path
 * capped at 10 results across ALL blogs, crowding HC articles out before
 * the cat-* filter ran. The index is HC-only by construction (blog.articles
 * inside the help-center blog) and covers all 57+ articles via paginate-250.
 *
 * Separate custom element from the theme's global <predictive-search>
 * (product-scoped, site header) — that component is untouched. Input is
 * name="hc-query" (NOT "q") so Doofinder can't grab it; submit handled here.
 */
if (!customElements.get('hc-predictive-search')) {
  const MAX_VISIBLE = 8;
  const DEBOUNCE_MS = 150;
  const FIELD = { titleWord: 100, titleSub: 60, exWord: 25, exSub: 12, tagWord: 30, tagSub: 15, fuzzy: 8 };

  // Edit distance <= 1 (cheap early-out). Used only when an exact/substring
  // match fails, for tokens >= 4 chars, against title/tag words.
  function lev1(a, b) {
    if (a === b) return true;
    const la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++edits > 1) return false;
      if (la > lb) i++; else if (lb > la) j++; else { i++; j++; }
    }
    if (i < la || j < lb) edits++;
    return edits <= 1;
  }

  class HCPredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = -1;
      this.matches = [];
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-hc-predictive-search]');
      if (!this.input || !this.panel) return;
      this.index = this.loadIndex();

      const form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          // Enter with a highlighted option opens it; otherwise render full results.
          if (this.activeIndex > -1 && this.matches[this.activeIndex]) {
            window.location.href = this.matches[this.activeIndex].u;
            return;
          }
          const q = this.input.value.trim();
          if (q) { this.close(); this.renderFullResults(q); }
        });
      }

      this.input.addEventListener('input', this.debounce(() => this.onChange(), DEBOUNCE_MS));
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.onChange(); });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('click', (event) => { if (!this.contains(event.target)) this.close(); });

      const clearBtn = this.querySelector('[data-hc-results-clear]');
      if (clearBtn) clearBtn.addEventListener('click', () => this.clearResults());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.hasAttribute('open')) {
          const region = this.querySelector('[data-hc-results]');
          if (region && !region.hidden) this.clearResults();
        }
      });
      try {
        const hcq = (new URLSearchParams(location.search).get('hcq') || '').trim();
        if (hcq) { this.input.value = hcq; this.renderFullResults(hcq); }
      } catch (_) {}
    }

    loadIndex() {
      const el = this.querySelector('#hc-search-index');
      if (!el) return [];
      let raw;
      try { raw = JSON.parse(el.textContent); } catch (_) { return []; }
      return (raw || []).map((a) => ({
        t: a.t || '', u: a.u || '', x: a.x || '', g: a.g || [],
        _t: (a.t || '').toLowerCase(),
        _x: (a.x || '').toLowerCase(),
        _g: (a.g || []).join(' ').toLowerCase()
      }));
    }

    debounce(fn, wait) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
    }

    onChange() {
      const q = this.input.value.trim().toLowerCase();
      if (!q) { this.close(); return; }
      this.matches = this.search(q);
      this.render(this.matches.slice(0, MAX_VISIBLE), this.input.value.trim());
    }

    search(q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      const scored = [];
      for (const a of this.index) {
        let total = 0, allHit = true;
        for (const tok of tokens) {
          let s = 0;
          const wb = new RegExp('\\b' + tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
          if (wb.test(a._t)) s = FIELD.titleWord; else if (a._t.includes(tok)) s = FIELD.titleSub;
          if (wb.test(a._g)) s = Math.max(s, FIELD.tagWord); else if (a._g.includes(tok)) s = Math.max(s, FIELD.tagSub);
          if (wb.test(a._x)) s = Math.max(s, FIELD.exWord); else if (a._x.includes(tok)) s = Math.max(s, FIELD.exSub);
          if (s === 0 && tok.length >= 4) {
            const cands = (a._t + ' ' + a._g).split(/\s+/);
            for (const c of cands) { if (lev1(tok, c)) { s = FIELD.fuzzy; break; } }
          }
          if (s === 0) { allHit = false; break; }
          total += s;
        }
        if (allHit && total > 0) scored.push({ ref: a, score: total });
      }
      scored.sort((x, y) => y.score - x.score || x.ref.t.length - y.ref.t.length);
      return scored.map((o) => o.ref);
    }

    render(items, rawQuery) {
      this.panel.textContent = '';
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      const ul = document.createElement('ul');
      ul.id = 'hc-predictive-results-list';
      ul.className = 'hc-predictive__list';
      ul.setAttribute('role', 'listbox');
      if (!items.length) {
        const li = document.createElement('li');
        li.className = 'hc-predictive__empty';
        li.appendChild(document.createTextNode(`No help articles match “${rawQuery}”. Try a different term or `));
        const browse = document.createElement('a');
        browse.href = '/blogs/help-center';
        browse.textContent = 'browse all articles';
        li.appendChild(browse);
        li.appendChild(document.createTextNode('.'));
        ul.appendChild(li);
      } else {
        items.forEach((a, i) => {
          const li = document.createElement('li');
          li.className = 'hc-predictive__item';
          li.id = 'hc-pred-opt-' + i;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          const link = document.createElement('a');
          link.className = 'hc-predictive__link';
          link.href = a.u;
          link.textContent = a.t; // XSS-safe: never innerHTML of title/query
          li.appendChild(link);
          li.addEventListener('mousemove', () => this.setActive(i));
          ul.appendChild(li);
        });
      }
      this.panel.appendChild(ul);
      this.open();
    }

    onKeydown(event) {
      const visible = Math.min(this.matches.length, MAX_VISIBLE);
      if (event.key === 'Escape') { this.close(); this.input.removeAttribute('aria-activedescendant'); return; }
      if (!this.hasAttribute('open') || !visible) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); this.setActive((this.activeIndex + 1) % visible); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); this.setActive((this.activeIndex - 1 + visible) % visible); }
      else if (event.key === 'Enter' && this.activeIndex > -1) { event.preventDefault(); window.location.href = this.matches[this.activeIndex].u; }
    }

    setActive(i) {
      const items = this.panel.querySelectorAll('.hc-predictive__item');
      items.forEach((li, idx) => {
        const on = idx === i;
        li.classList.toggle('is-active', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.activeIndex = i;
      if (items[i]) this.input.setAttribute('aria-activedescendant', items[i].id);
    }

    open() { this.setAttribute('open', ''); this.input.setAttribute('aria-expanded', 'true'); }
    close() { this.removeAttribute('open'); this.input.setAttribute('aria-expanded', 'false'); this.activeIndex = -1; }

    // Full HC-exclusive results view (Task #7 v2). Reuses this.search()/this.index
    // verbatim — no second index, no second matcher, no MAX_VISIBLE slice.
    renderFullResults(rawQuery) {
      // Clear the pre-paint guard up-front so even an early return below can't
      // leave the grid hidden. The happy path re-hides via data-hc-results-active.
      document.documentElement.removeAttribute('data-hc-prepaint-results');
      const results = this.search(rawQuery.toLowerCase());
      const region = this.querySelector('[data-hc-results]');
      const list = this.querySelector('[data-hc-results-list]');
      const heading = this.querySelector('#hc-results-heading');
      const status = this.querySelector('[data-hc-results-status]');
      const root = document.querySelector('.hc-landing');
      if (!region || !list || !heading) return;
      list.textContent = '';
      const n = results.length;
      heading.textContent = 'Showing ' + n + ' help article' + (n === 1 ? '' : 's') +
        ' for “' + rawQuery + '”'; // textContent — XSS-safe
      if (status) {
        status.textContent = n === 0
          ? 'No help articles found for ' + rawQuery
          : n + ' help article' + (n === 1 ? '' : 's') + ' found.';
      }
      if (n === 0) {
        const li = document.createElement('li');
        li.className = 'hc-results__empty';
        li.appendChild(document.createTextNode('No help articles match your search. Try fewer or different words, or '));
        const a = document.createElement('a');
        a.href = '/blogs/help-center';
        a.textContent = 'browse all articles';
        li.appendChild(a);
        li.appendChild(document.createTextNode('.'));
        list.appendChild(li);
      } else {
        results.forEach((a) => {
          const li = document.createElement('li');
          const card = document.createElement('a');
          card.className = 'hc-kb-card hc-results__card';
          card.href = a.u;
          const body = document.createElement('span');
          body.className = 'hc-kb-card__body';
          const title = document.createElement('h3');
          title.className = 'hc-kb-card__title';
          title.textContent = a.t; // XSS-safe
          body.appendChild(title);
          if (a.x) {
            const ex = document.createElement('span');
            ex.className = 'hc-kb-card__excerpt';
            ex.textContent = a.x; // XSS-safe
            body.appendChild(ex);
          }
          card.appendChild(body);
          li.appendChild(card);
          list.appendChild(li);
        });
      }
      region.hidden = false;
      if (root) root.setAttribute('data-hc-results-active', '');
      this.injectNoindex();
      try {
        history.replaceState(null, '', location.pathname + '?hcq=' + encodeURIComponent(rawQuery));
      } catch (_) {}
      heading.focus(); // tabindex=-1: SR + keyboard land on the count
    }

    injectNoindex() {
      if (document.querySelector('meta[name="robots"][data-hc-results-noindex]')) return;
      const m = document.createElement('meta');
      m.name = 'robots';
      m.content = 'noindex,follow';
      m.setAttribute('data-hc-results-noindex', '');
      document.head.appendChild(m);
      const canon = document.querySelector('link[rel="canonical"]');
      if (canon) canon.setAttribute('href', location.origin + '/blogs/help-center');
    }

    clearResults() {
      const root = document.querySelector('.hc-landing');
      const region = this.querySelector('[data-hc-results]');
      if (region) region.hidden = true;
      if (root) root.removeAttribute('data-hc-results-active');
      this.input.value = '';
      const m = document.querySelector('meta[name="robots"][data-hc-results-noindex]');
      if (m) m.remove();
      try { history.replaceState(null, '', location.pathname); } catch (_) {}
      this.input.focus();
    }
  }

  customElements.define('hc-predictive-search', HCPredictiveSearch);
}
